<template>
  <a-modal
    title="Copy RBAC from user"
    :visible="visible"
    :confirmLoading="isCopying"
    :maskClosable="false"
    :okButtonProps="{ props: { disabled: !canCopy } }"
    okText="Copy"
    @ok="onCopy"
    @cancel="onCancel"
  >
    <a-select
      v-model="sourceId"
      show-search
      allow-clear
      class="full-w"
      placeholder="Select source user"
      option-filter-prop="children"
      :loading="isLoadingUsers"
      :disabled="isCopying"
    >
      <a-select-option v-for="user in sourceOptions" :key="user.customerId" :value="user.customerId">
        {{ user.userName }} (#{{ user.customerId }})
      </a-select-option>
    </a-select>
    <a-radio-group v-model="mode" class="mode-group" :disabled="isCopying">
      <a-radio :value="COPY_MODES.Add">
        Add to existing
      </a-radio>
      <a-radio :value="COPY_MODES.Replace">
        Replace
      </a-radio>
    </a-radio-group>
    <template v-if="sourceUser">
      <p class="summary">
        {{ summary }}
      </p>
      <template v-if="canCopy">
        <p class="summary summary--detail">
          Grants: {{ grantNamesText }}
        </p>
        <p class="summary summary--detail">
          Target users: {{ targetNamesText }}
        </p>
      </template>
      <p v-if="canCopy && mode === COPY_MODES.Replace" class="summary summary--warning">
        Grants the target users have that the source does not have will be removed.
      </p>
    </template>
  </a-modal>
</template>

<script>
  import UserManagementService from '../services/UserManagement.service';
  import { COPY_MODES, copyRbacToUser, formatNames, planCopy, resolveSourceGrantIds } from '../helpers/copyRbac';
  import { runSequentially } from '../helpers/runSequentially';

  export default {
    name: 'CopyRbacModal',
    props: {
      visible: {
        type: Boolean,
        default: false,
      },
      targets: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        COPY_MODES,
        users: [],
        isLoadingUsers: false,
        sourceId: undefined,
        mode: COPY_MODES.Add,
        isCopying: false,
      };
    },
    computed: {
      sourceOptions() {
        const targetIds = new Set(this.targets.map((target) => target.customerId));
        return this.users.filter((user) => !targetIds.has(user.customerId));
      },
      sourceUser() {
        return this.sourceOptions.find((user) => user.customerId === this.sourceId);
      },
      sourceGrantIds() {
        if (!this.sourceUser) {
          return [];
        }
        return resolveSourceGrantIds(this.sourceUser, this.users);
      },
      knownIds() {
        return new Set(this.users.map((user) => user.customerId));
      },
      canCopy() {
        return this.sourceGrantIds.length > 0;
      },
      grantNamesText() {
        const grantIds = new Set(this.sourceGrantIds);
        return formatNames(this.users.filter((user) => grantIds.has(user.customerId)).map((user) => user.userName));
      },
      targetNamesText() {
        return formatNames(this.targets.map((target) => target.userName));
      },
      summary() {
        if (!this.canCopy) {
          return 'The selected user has no grants that can be copied.';
        }
        return `${this.sourceGrantIds.length} grant(s) from ${this.sourceUser.userName} will be copied to ${this.targets.length} user(s).`;
      },
    },
    watch: {
      visible(isVisible) {
        if (isVisible) {
          this.loadUsers();
        }
      },
    },
    methods: {
      async loadUsers() {
        this.users = [];
        this.isLoadingUsers = true;
        try {
          this.users = await UserManagementService.fetchListUsers();
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
          this.$emit('close');
        } finally {
          this.isLoadingUsers = false;
        }
      },
      // The list loaded when the modal opened is newer than the grants the caller passed in.
      getCurrentIds(target) {
        const freshUser = this.users.find((user) => user.customerId === target.customerId);
        return ((freshUser || target).roleBasedAccessControl || []).map(Number);
      },
      reset() {
        this.sourceId = undefined;
        this.mode = COPY_MODES.Add;
      },
      onCopy() {
        if (this.mode === COPY_MODES.Replace) {
          const plans = this.targets.map((target) =>
            planCopy(target.customerId, this.getCurrentIds(target), this.sourceGrantIds, COPY_MODES.Replace, this.knownIds)
          );
          // Nothing is removed, so there is nothing to confirm.
          if (plans.some((plan) => plan.toRemove.length)) {
            this.confirmReplace(plans);
            return;
          }
        }
        this.runCopy();
      },
      confirmReplace(plans) {
        const removeCount = plans.reduce((sum, plan) => sum + plan.toRemove.length, 0);
        const addCount = plans.reduce((sum, plan) => sum + plan.toAdd.length, 0);
        const losingUserCount = plans.filter((plan) => plan.toRemove.length).length;
        this.$confirm({
          title: 'Replace RBAC grants?',
          content: `This removes ${removeCount} grant(s) across ${losingUserCount} user(s) and adds ${addCount}. Grants the target users have that the source does not have will be removed.`,
          okText: 'Replace',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: () => this.runCopy(),
        });
      },
      async runCopy() {
        const { targets, sourceGrantIds, mode, knownIds } = this;
        this.isCopying = true;
        try {
          const { failedIds, reason, stoppedBy } = await runSequentially(
            targets.map((target) => target.customerId),
            (id) => {
              const target = targets.find((candidate) => candidate.customerId === id);
              return copyRbacToUser(id, this.getCurrentIds(target), sourceGrantIds, mode, knownIds);
            }
          );
          this.notifyResult(targets.length - failedIds.length, targets.length, reason);
          this.reset();
          this.$emit('done', { failedIds, stoppedBy });
        } finally {
          this.isCopying = false;
        }
      },
      notifyResult(succeededCount, totalCount, reason) {
        const failedCount = totalCount - succeededCount;
        const reasonSuffix = reason ? ` ${reason}` : '';
        if (!failedCount) {
          this.$showSuccessMessage(`Copied RBAC to ${succeededCount} user(s).`);
        } else if (!succeededCount) {
          this.$showErrorMessage(`Failed to copy RBAC.${reasonSuffix}`);
        } else {
          this.$showWarningMessage(`Copied RBAC to ${succeededCount} of ${totalCount} user(s); ${failedCount} failed.${reasonSuffix}`);
        }
      },
      onCancel() {
        if (this.isCopying) {
          return;
        }
        this.reset();
        this.$emit('close');
      },
    },
  };
</script>

<style lang="scss" scoped>
  .mode-group {
    margin-top: 16px;
  }
  .summary {
    margin: 16px 0 0;
    &--detail {
      margin-top: 4px;
    }
    &--warning {
      margin-top: 8px;
      color: #d46b08;
    }
  }
</style>
